import React, { Fragment, useCallback, useEffect, useState } from "react";
import Breadcrumb from "../common/breadcrumb";
import { useSelector } from "react-redux";
import { getContactMessages, markContactMessageRead } from "../../utils/contactApi";
import { successToast } from "../../shared/Toast";
import { C, tbl, reportingGlobalCSS } from "../reporting/reportingStyles";
import { RCard, Badge, Loader, TablePagination } from "../reporting/ReportingShared";

const filterBtn = (active) => ({
    background: active ? C.coral : C.white,
    color: active ? C.white : C.textMed,
    border: `1.5px solid ${active ? C.coral : C.border}`,
    borderRadius: 10,
    fontWeight: 600,
    fontSize: 13,
    padding: "7px 14px",
    cursor: "pointer",
});

const markReadBtn = {
    background: C.white,
    border: `1px solid ${C.coral}`,
    borderRadius: 8,
    fontWeight: 600,
    fontSize: 12,
    color: C.coral,
    padding: "5px 12px",
    cursor: "pointer",
};

const FILTERS = [
    { value: "", label: "All" },
    { value: "new", label: "New" },
    { value: "read", label: "Read" },
];

const ContactMessages = () => {
    const userType = localStorage.getItem("userType");
    const merchantID = localStorage.getItem("MerchantId");
    const { selectedMerchant: reduxSelectedMerchant } = useSelector((state) => state.merchant || {});
    const selectedMerchant = userType === "merchant" ? merchantID : reduxSelectedMerchant;

    const [isLoading, setIsLoading] = useState(false);
    const [messages, setMessages] = useState([]);
    const [statusFilter, setStatusFilter] = useState("");
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(20);
    const [total, setTotal] = useState(0);

    const merchantSelected = selectedMerchant && selectedMerchant !== "all";

    const fetchMessages = useCallback(async () => {
        if (!merchantSelected) {
            setMessages([]);
            setTotal(0);
            return;
        }
        setIsLoading(true);
        const data = await getContactMessages({
            merchantId: selectedMerchant,
            status: statusFilter || undefined,
            page,
            limit,
        });
        setMessages(data?.messages || []);
        setTotal(data?.total || 0);
        setIsLoading(false);
    }, [merchantSelected, selectedMerchant, statusFilter, page, limit]);

    useEffect(() => {
        fetchMessages();
    }, [fetchMessages]);

    const handleMarkRead = async (id) => {
        const result = await markContactMessageRead(id);
        if (result) {
            successToast("Marked as read.");
            fetchMessages();
        }
    };

    return (
        <Fragment>
            <Breadcrumb title="Contact Messages" parent="Merchants" />
            <style>{reportingGlobalCSS}</style>
            <div className="rpt-wrap" style={{ padding: "0 24px 24px" }}>
                <RCard
                    title={
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
                            <span>Contact Messages</span>
                            <div style={{ display: "flex", gap: 8 }}>
                                {FILTERS.map((f) => (
                                    <button
                                        key={f.value}
                                        style={filterBtn(statusFilter === f.value)}
                                        onClick={() => {
                                            setPage(1);
                                            setStatusFilter(f.value);
                                        }}
                                    >
                                        {f.label}
                                    </button>
                                ))}
                            </div>
                        </div>
                    }
                >
                    {isLoading ? (
                        <Loader />
                    ) : messages.length === 0 ? (
                        <div style={{ textAlign: "center", padding: "40px", color: C.textMed }}>
                            {!merchantSelected
                                ? "Please select a merchant to view contact messages."
                                : "No contact messages found."}
                        </div>
                    ) : (
                        <>
                            <div style={{ overflowX: "auto" }}>
                                <table style={tbl.table}>
                                    <thead>
                                        <tr>
                                            <th style={tbl.th}>ID</th>
                                            <th style={tbl.th}>Name</th>
                                            <th style={tbl.th}>Email</th>
                                            <th style={tbl.th}>Phone</th>
                                            <th style={tbl.th}>Message</th>
                                            <th style={tbl.th}>Status</th>
                                            <th style={tbl.th}>Received</th>
                                            <th style={tbl.th}>Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {messages.map((msg) => (
                                            <tr key={msg.id} className="rpt-row">
                                                <td style={tbl.td}>{msg.id}</td>
                                                <td style={{ ...tbl.td, fontWeight: msg.status === "new" ? 600 : 400 }}>
                                                    {msg.name}
                                                </td>
                                                <td style={tbl.td}>
                                                    <a href={`mailto:${msg.email}`} style={{ color: C.coral }}>
                                                        {msg.email}
                                                    </a>
                                                </td>
                                                <td style={tbl.td}>{msg.phone || "—"}</td>
                                                <td style={{ ...tbl.td, maxWidth: 360, whiteSpace: "pre-wrap" }}>
                                                    {msg.message}
                                                </td>
                                                <td style={tbl.td}>
                                                    <Badge
                                                        color={msg.status === "new" ? C.coral : C.textMed}
                                                        bg={msg.status === "new" ? C.coralLight : C.borderLight}
                                                    >
                                                        {msg.status === "new" ? "NEW" : "READ"}
                                                    </Badge>
                                                </td>
                                                <td style={{ ...tbl.td, whiteSpace: "nowrap" }}>
                                                    {new Date(msg.createdAt).toLocaleString("en-GB", {
                                                        day: "2-digit",
                                                        month: "short",
                                                        year: "numeric",
                                                        hour: "2-digit",
                                                        minute: "2-digit",
                                                    })}
                                                </td>
                                                <td style={tbl.td}>
                                                    {msg.status === "new" && (
                                                        <button style={markReadBtn} onClick={() => handleMarkRead(msg.id)}>
                                                            Mark Read
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            <TablePagination
                                total={total}
                                page={page}
                                limit={limit}
                                onPageChange={setPage}
                                onLimitChange={setLimit}
                            />
                        </>
                    )}
                </RCard>
            </div>
        </Fragment>
    );
};

export default ContactMessages;
